import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Calendar, Umbrella, CalendarDays } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const DAYS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MONTHS_FR = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

export default function CalendrierInstallateur() {
  const { user } = useAuth();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [conges, setConges] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!user?.employee_id) return;
    // Charger les congés approuvés
    axios.get(`${API_URL}/api/conges?employee_id=${user.employee_id}&statut=approuve`)
      .then(r => r.status === 200 ? r.data : [])
      .then(d => setConges(Array.isArray(d) ? d : d.conges || []))
      .catch(() => {});
    // Charger les congés rétroactifs depuis la fiche employé
    axios.get(`${API_URL}/api/employees/${user.employee_id}`)
      .then(r => r.status === 200 ? r.data : null)
      .then(d => {
        if (d?.conges_retroactifs?.length > 0) {
          const retro = d.conges_retroactifs.map(c => ({
            ...c,
            statut: 'approuve',
            est_retroactif: true,
          }));
          setConges(prev => [...prev, ...retro]);
        }
      })
      .catch(() => {});
    // Charger les événements
    axios.get(`${API_URL}/api/agenda-perso?employee_id=${user.employee_id}`)
      .then(r => r.status === 200 ? r.data : [])
      .then(d => setEvents(Array.isArray(d) ? d : d.events || []))
      .catch(() => {});
  }, [user, month, year]);

  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    const dayConges = conges.filter(c => {
      const d = c.date_debut?.substring(0, 10) || '';
      const f = c.date_fin?.substring(0, 10) || d;
      return dateStr >= d && dateStr <= f;
    });
    const dayEvents = events.filter(e => {
      const d = (e.date || e.date_debut || '').substring(0, 10);
      return d === dateStr;
    });
    return { conges: dayConges, events: dayEvents };
  };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="calendrier-installateur">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Mon Planning</h1>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <CardTitle className="text-base">{MONTHS_FR[month]} {year}</CardTitle>
            <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg"><ChevronRight className="w-5 h-5" /></button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map(d => <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
              const isToday = dateStr === todayStr;
              const { conges: dc, events: de } = getEventsForDay(day);
              const hasConge = dc.length > 0;
              const hasEvent = de.length > 0;
              return (
                <div key={day} className={`relative text-center py-2 rounded-lg text-sm cursor-default transition-colors
                  ${isToday ? 'bg-blue-600 text-white font-bold' : ''}
                  ${hasConge && !isToday ? 'bg-amber-100 text-amber-800' : ''}
                  ${hasEvent && !hasConge && !isToday ? 'bg-purple-100 text-purple-800' : ''}
                  ${!hasConge && !hasEvent && !isToday ? 'hover:bg-slate-50' : ''}
                `}>
                  {day}
                  {(hasConge || hasEvent) && (
                    <div className="flex justify-center gap-0.5 mt-0.5">
                      {hasConge && <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                      {hasEvent && <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Légende */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Congé
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <div className="w-2.5 h-2.5 rounded-full bg-purple-500" /> Événement
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-600" /> Aujourd'hui
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
